import { getLocalizedValue } from "@repo/shared";
import type {
  CostOverrideMode,
  LocalizedString,
  PortfolioCostOverrideInfo,
  Translated,
} from "@repo/shared/types";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { cn } from "@/lib/utils";
import { type CostOverrideEdit, OverriddenCostValue } from "./cost-override";
import { T } from "./portfolio-costs-widget.i18n";

export type PortfolioCostBracket = {
  id: string;
  startValue: number;
  value: number;
  order: number;
};

export type PortfolioCost = {
  id: string;
  nameI18n: LocalizedString;
  costType: string; // "amount" | "pct"
  category: "RECURRING" | "DEPOSIT" | "WITHDRAWAL";
  order: number;
  brackets: PortfolioCostBracket[];
  // Manual flat fee: a single percentage with an optional minimum-fee floor.
  isManual?: boolean;
  valuePct?: number | null;
  minFeeAmount?: number | null;
  /** QL-20 — whether the advisor may override this fee in a flow instance. */
  advisorOverride?: CostOverrideMode;
  /** QL-20 — set when the instance overrides this fee: the catalogue figures. */
  override?: PortfolioCostOverrideInfo | null;
};

type PortfolioCostsWidgetProps = {
  portfolioCosts: PortfolioCost[];
  title?: string;
  currency?: string;
  /**
   * QL-20 — advisor only, on an editable instance: opens the override dialog
   * for a fee that allows it, or (reset only) for an override that no longer
   * could be made. Absent = read-only (client flow).
   */
  onEditCost?: (edit: CostOverrideEdit) => void;
  /** QL-20 — the tenant's cost_overrides feature is on, so new overrides can be made. */
  canOverride?: boolean;
};

// Category labels are now translated in the component

/**
 * Format a currency amount
 */
type FormatNumberFn = (value: number, options?: { maximumFractionDigits?: number }) => string;

function formatAmount(value: number, fmt: FormatNumberFn, compact = false): string {
  if (compact && value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (compact && value >= 1000) {
    return `${(value / 1000).toFixed(0)}k`;
  }
  return fmt(value);
}

/**
 * Format bracket range for display in modal
 */
function formatBracketRange(
  bracket: PortfolioCostBracket,
  nextBracket: PortfolioCostBracket | null,
  fmt: FormatNumberFn,
): string {
  const start = formatAmount(bracket.startValue, fmt);
  if (!nextBracket) {
    return `${start}+`;
  }
  const end = formatAmount(nextBracket.startValue - 1, fmt);
  return `${start} - ${end}`;
}

/**
 * Format the cost value based on type
 */
function formatCostValue(
  value: number,
  costType: string,
  fmt: FormatNumberFn,
  currency?: string,
): string {
  if (costType === "pct") {
    return `${value}%`;
  }
  return `${formatAmount(value, fmt)} ${currency || ""}`.trim();
}

/**
 * Get fee range display (e.g., "0.18-0.25%" or "50-100 NOK" for tiered brackets)
 * Shows low to high order
 */
function getFeeRangeDisplay(cost: PortfolioCost, fmt: FormatNumberFn, currency?: string): string {
  const { brackets, costType } = cost;

  // Manual flat fee: single percentage with an optional minimum-fee floor.
  if (cost.isManual) {
    const pct = cost.valuePct != null ? `${cost.valuePct}%` : "-";
    if (cost.minFeeAmount != null && cost.minFeeAmount > 0) {
      return `${pct} (min ${formatAmount(cost.minFeeAmount, fmt)} ${currency || ""})`.trim();
    }
    return pct;
  }

  if (brackets.length === 0) {
    return "-";
  }

  if (brackets.length === 1) {
    return formatCostValue(brackets[0].value, costType, fmt, currency);
  }

  // Get min and max values - show low to high
  const values = brackets.map((b) => b.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  if (costType === "pct") {
    return `${minValue}-${maxValue}%`;
  }
  return `${formatAmount(minValue, fmt)}-${formatAmount(maxValue, fmt)} ${currency || ""}`.trim();
}

/**
 * Bracket details modal
 */
function BracketDetailsModal({
  cost,
  open,
  onOpenChange,
  currency,
  translations,
  language,
}: {
  cost: PortfolioCost | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currency?: string;
  translations: Translated<typeof T>;
  language: string;
}) {
  const { tenantDefaultLanguage } = useUserLanguage();
  const { formatNumber } = useRegionalFormat();
  if (!cost) return null;

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "RECURRING":
        return translations.recurring;
      case "DEPOSIT":
        return translations.onDeposit;
      case "WITHDRAWAL":
        return translations.onWithdrawal;
      default:
        return category;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {getLocalizedValue(cost.nameI18n, language, tenantDefaultLanguage)}
          </DialogTitle>
          <DialogDescription>
            {cost.costType === "pct"
              ? translations.percentageBasedFee
              : translations.fixedAmountFee}{" "}
            • {getCategoryLabel(cost.category)}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{translations.portfolioValue}</TableHead>
                <TableHead className="text-right">
                  {cost.costType === "pct" ? translations.rate : translations.fee}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cost.brackets.map((bracket, index) => {
                const nextBracket = cost.brackets[index + 1] || null;
                return (
                  <TableRow key={bracket.id}>
                    <TableCell className="font-medium">
                      {formatBracketRange(bracket, nextBracket, formatNumber)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCostValue(bracket.value, cost.costType, formatNumber, currency)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PortfolioCostsWidget({
  portfolioCosts,
  title,
  currency,
  onEditCost,
  canOverride = false,
}: PortfolioCostsWidgetProps) {
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const { formatNumber } = useRegionalFormat();
  const t = useT(T);

  const [selectedCost, setSelectedCost] = useState<PortfolioCost | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "RECURRING":
        return t.recurring;
      case "DEPOSIT":
        return t.onDeposit;
      case "WITHDRAWAL":
        return t.onWithdrawal;
      default:
        return category;
    }
  };

  if (portfolioCosts.length === 0) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">{t.noPortfolioCostsConfigured}</p>
      </Card>
    );
  }

  // QL-20 — the catalogue figures of an overridden fee, formatted the same way.
  const catalogueDisplay = (cost: PortfolioCost) =>
    getFeeRangeDisplay(
      cost.override
        ? {
            ...cost,
            valuePct: cost.override.originalValuePct,
            brackets: cost.override.originalBrackets,
          }
        : cost,
      formatNumber,
      currency,
    );

  const renderRate = (cost: PortfolioCost) => {
    const rate = getFeeRangeDisplay(cost, formatNumber, currency);
    const shown = cost.override ? (
      <OverriddenCostValue
        value={rate}
        original={catalogueDisplay(cost)}
        note={cost.override.note}
      />
    ) : (
      rate
    );
    const mode = cost.advisorOverride ?? "off";
    const resetOnly = !(canOverride && mode !== "off");
    if (!onEditCost || (resetOnly && !cost.override)) return shown;
    const isPct = cost.isManual || cost.costType === "pct";
    // One figure to prefill: the manual percent, or a one-bracket fee's value.
    const single = cost.isManual
      ? (cost.valuePct ?? null)
      : cost.brackets.length === 1
        ? cost.brackets[0].value
        : null;
    return (
      <span className="inline-flex items-center justify-end gap-1">
        {shown}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          tooltip={t.overrideFee(localize(cost.nameI18n))}
          onClick={(e) => {
            // The row itself opens the tier details.
            e.stopPropagation();
            onEditCost({
              request: { kind: "portfolio_cost", portfolioCostId: cost.id },
              dialog: {
                label: localize(cost.nameI18n),
                unit: isPct ? "pct" : "amount",
                unitLabel: isPct ? "%" : currency || "",
                value: single,
                catalogueLabel: catalogueDisplay(cost),
                mode,
                note: cost.override?.note ?? null,
                overridden: !!cost.override,
                resetOnly,
              },
            });
          }}
        >
          <Pencil className="h-3 w-3" />
        </Button>
      </span>
    );
  };

  const handleRowClick = (cost: PortfolioCost) => {
    // Only show modal if there are multiple brackets
    if (cost.brackets.length > 1) {
      setSelectedCost(cost);
      setModalOpen(true);
    }
  };

  return (
    <div className="w-full space-y-4">
      <FlowComponentHeader title={title} />

      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.fee}</TableHead>
            <TableHead>{t.category}</TableHead>
            <TableHead className="text-right">{t.rate}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {portfolioCosts.map((cost) => {
            const hasMultipleBrackets = cost.brackets.length > 1;
            return (
              <TableRow
                key={cost.id}
                className={cn(hasMultipleBrackets && "cursor-pointer hover:bg-muted/50")}
                onClick={() => handleRowClick(cost)}
              >
                <TableCell className="font-medium">
                  {localize(cost.nameI18n)}
                  {hasMultipleBrackets && (
                    <span className="ml-2 text-xs text-muted-foreground">{t.tiered}</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {getCategoryLabel(cost.category)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono">{renderRate(cost)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <BracketDetailsModal
        cost={selectedCost}
        open={modalOpen}
        onOpenChange={setModalOpen}
        currency={currency}
        translations={t}
        language={userLanguage}
      />
    </div>
  );
}
